import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Portal } from 'react-portal';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { Container, Segment, Button, Message } from 'semantic-ui-react';
import { defineMessages, injectIntl } from 'react-intl';
import Error from '@plone/volto/components/theme/Error/Error';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import backSVG from '@plone/volto/icons/back.svg';
import {
  updateProduktkategorie,
  getProduktkategorieByID,
  getProduktkategorien,
  deleteProduktkategorie,
  getVistPermissions,
} from '../../../actions';
import { hasManagementUiAccess } from '../../../helpers/permissions';

import ButtonArea from '../ButtonArea';

import ProduktkategorieForm from './ProduktkategorieForm';

const messages = defineMessages({
  back: {
    id: 'Back',
    defaultMessage: 'Back',
  },
});

const ProduktkategorieEdit = ({
  pathname,
  produktkategorie,
  produktkategorien,
  getProduktkategorien,
  getProduktkategorieByID,
  updateProduktkategorie,
  deleteProduktkategorie,
  intl,
  token,
  permissions = null,
  getVistPermissions,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    pid: '',
    id: undefined,
    level: '',
  });

  const location = useLocation();
  const [error, setError] = useState(null);
  const [isClient, setIsClient] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [isProduktkategorieNameDuplicate, setIsProduktkategorieNameDuplicate] =
    useState(false);
  const isSubmitDisabled = formData.name === '';

  const history = useHistory();
  // Management-table CRUD: Admin + F&E per TASK-90 §2.
  const canEdit = hasManagementUiAccess(permissions);
  // flag to make sure permissions are fully loaded before returning
  // an unauthorized error
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  // load permissions
  useEffect(() => {
    setIsClient(true);
    // Fetch @vist_permissions if the reducer hasn't seen a response yet.
    if (!permissions && permissionsLoading) {
      getVistPermissions()
        .then(() => {
          setPermissionsLoading(false);
        })
        .catch(() => {
          setError({ status: 401 });
          setPermissionsLoading(false);
        });
    }
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    setIsClient(true);
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('id');
    getProduktkategorien({ showAll: true });
    getProduktkategorieByID(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getProduktkategorieByID, getProduktkategorien]);

  useEffect(() => {
    if (produktkategorie) {
      setFormData({
        name: produktkategorie.name,
        pid: produktkategorie.pid,
        id: produktkategorie.id,
        leve: produktkategorie.level,
      });
    }
  }, [produktkategorie]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));

    if (name === 'name' && value !== produktkategorie.name) {
      const isDuplicate = produktkategorien.some(
        (produktkategorie) => produktkategorie.name === value,
      );
      setIsProduktkategorieNameDuplicate(isDuplicate);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('id');

    try {
      await updateProduktkategorie(id, formData);
      history.push(`/controlpanel/rezeptdatenbank/produktkategorien`);
      // Handle success or show a success message
    } catch (error) {
      // Handle error during update
      setError(error);
    }
  };

  const handleDelete = async () => {
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('id');

    try {
      await deleteProduktkategorie(id);
      setDeleteSuccess(true);
    } catch (error) {
      // Handle delete error
      setError(error);
    }
  };

  if (!token) {
    return <Error error={{ status: 401 }} />;
  }
  if (!permissionsLoading && !canEdit) {
    return <Error error={{ status: 401 }} />;
  }
  return (
    <div className="view-wrapper">
      <Helmet title="Produktkategorie Bearbeiten" />
      <Container className="controlpanel">
        <Segment.Group raised>
          <Segment className="primary">
            <div className="rezeptdatenbank header">
              <div className="header-container">
                <h1>{produktkategorie && produktkategorie.name} </h1>
              </div>
              <div className="button-container">
                <ButtonArea
                  handleSubmit={handleSubmit}
                  handleDelete={handleDelete}
                  isSubmitDisabled={isSubmitDisabled}
                  backURL={'/controlpanel/rezeptdatenbank/produktkategorien'}
                  typeString={'die Produktkategorie'}
                />
              </div>
            </div>
          </Segment>
          <Segment className="rdbAddEditForm">
            {error && (
              <Message negative>
                <Message.Header>Error</Message.Header>
                <p>{error.message}</p>
              </Message>
            )}
            {deleteSuccess ? (
              <Message positive>
                <Message.Header>Produktkategorie wurde gelöscht</Message.Header>
                <Button
                  primary
                  as={Link}
                  to="/controlpanel/rezeptdatenbank/produktkategorien"
                >
                  Zurück zur Produktkategorien-Übersicht
                </Button>
              </Message>
            ) : (
              <>
                <div className="rdb edit form produktkategorien">
                  <ProduktkategorieForm
                    formData={formData}
                    isProduktkategorieNameDuplicate={
                      isProduktkategorieNameDuplicate
                    }
                    handleInputChange={handleInputChange}
                    kategorien={produktkategorien}
                  />
                </div>
              </>
            )}
          </Segment>
        </Segment.Group>
      </Container>
      {isClient && (
        <Portal node={document.getElementById('toolbar')}>
          <Toolbar
            pathname={pathname}
            hideDefaultViewButtons
            inner={
              <Link to="/controlpanel/rezeptdatenbank" className="item">
                <Icon
                  name={backSVG}
                  className="contents circled"
                  size="30px"
                  title={intl.formatMessage(messages.back)}
                />
              </Link>
            }
          />
        </Portal>
      )}
    </div>
  );
};

ProduktkategorieEdit.propTypes = {
  pathname: PropTypes.string.isRequired,
  produktkategorien: PropTypes.array.isRequired,
  produktkategorie: PropTypes.object,
  getProduktkategorieByID: PropTypes.func.isRequired,
  updateProduktkategorie: PropTypes.func.isRequired,
  deleteProduktkategorie: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  token: PropTypes.string,
  permissions: PropTypes.object,
  getVistPermissions: PropTypes.func,
};

export default compose(
  injectIntl,
  connect(
    (state) => ({
      pathname: state.router.location.pathname,
      produktkategorie: state.produktkategorieByID.data,
      produktkategorien: state.produktkategorien.data.produktkategorien,
      token: state.userSession.token,
      permissions: state.permissions.permissions,
    }),
    {
      updateProduktkategorie,
      getProduktkategorieByID,
      deleteProduktkategorie,
      getProduktkategorien,
      getVistPermissions,
    },
  ),
)(ProduktkategorieEdit);
